// @flow
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from '@locus-taxy/neo-ui';
import isEqual from 'lodash/isEqual';

import UnsavedChangesNavigationPrompt from 'common/components/core/form/UnsavedChangesNavigationPrompt';

import DisclaimerModal from './DisclaimerModal';
import Header from './Header';
import JsonForm from './form/JsonForm';
import { toTranslationKey } from './utils';

type Props = {
    initialValue: $FixMe,
    product: string,
    metadata: $FixMe,
    onSave: (value: $FixMe) => void,
    isSaving: boolean
};

const ProductSettingsFormBase = ({
    initialValue,
    metadata,
    product,
    onSave,
    isSaving
}: Props): React.Node => {
    const [{ value, errors }, setData] = React.useState({
        errors: null,
        value: initialValue
    });
    const [showDisclaimer, setShowDisclaimer] = React.useState(false);

    const {
        t,
        i18n: { language }
    } = useTranslation('platform');

    const hasUnsavedChanges = React.useMemo(() => {
        return !isEqual(value, initialValue);
    }, [value, initialValue]);

    const i18n = React.useMemo(() => {
        return {
            locale: language,
            // eslint-disable-next-line no-unused-vars
            translate: (key, origKey) => {
                const _key = toTranslationKey(product, key);

                // for now only translate if the key is `label` or `description`
                if (key.endsWith('label') || key.endsWith('description')) {
                    return t(_key);
                }
                return origKey;
            }
        };
    }, [language, product, t]);

    return (
        <Flex width="100%" backgroundColor="grey.100" flexDirection="column">
            <Header
                product={product}
                onSave={() => setShowDisclaimer(true)}
                isSaving={isSaving}
                disableSave={
                    !hasUnsavedChanges || Boolean(errors && errors.length > 0)
                }
            />
            <JsonForm
                metadata={metadata}
                value={value}
                i18n={i18n}
                onChange={(value, errors) => setData({ value, errors })}
            />
            <UnsavedChangesNavigationPrompt when={hasUnsavedChanges} />
            <DisclaimerModal
                isOpen={showDisclaimer}
                onConfirm={() => {
                    setShowDisclaimer(false);
                    onSave(value);
                }}
                onDismiss={() => setShowDisclaimer(false)}
            />
        </Flex>
    );
};

export default ProductSettingsFormBase;
